/**
 * Main Routes Configuration
 *
 * Structure:
 * - /login (public)
 * - / (protected, with guards)
 *   - Static pages (terms, privacy, etc.)
 *   - Other protected feature routes
 */

import { Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { Router } from '@uhg-abyss/web/ui/Router';
import { AppShell } from '../common/AppShell';
import { RequireAuth } from '../auth';



// ─── Feature Routes ─────────────────────────────────────────────────────────────
import { authRoutes } from './features/auth/authRoutes';
import { headRoutes } from './features/head/headRoutes';
import { featureRoutes } from './features';
import { PublicLayout, GuardedRoute } from './layout';
import { environment } from '@web/config';
import { RouteLoading } from './components/RouteLoading';
import { ChunkErrorBoundary } from './components/ChunkErrorBoundary';

// ─── Routes ───────────────────────────────────────────────────────────────────
const CatchAllRedirect = () => {

  const isExternal = !environment.internalAccess;
  if (isExternal) {
    return <Navigate to="/myinsights" replace />;
  }
  const hasProviderContext = !!sessionStorage.getItem('topFilterTin');
  return <Navigate to={hasProviderContext ? '/overview-page-advocate' : '/myhome'} replace />;
};
const publicHeadRoutes = headRoutes.filter(route => route.layout === 'public');
const protectedHeadRoutes = headRoutes.filter(route => route.layout !== 'public');

export const Routes = () => {
  return (
    <Router.Routes title="UHC Insights">
      {/* ──────────────────────────────────────────────────────────────── */
      /* PUBLIC ROUTES (no authentication required)                     */
      /* ──────────────────────────────────────────────────────────────── */}

      {/* Auth Routes */}
      {authRoutes.map(route => {
        if (!route.component) return null;
        return (
          <Router.Route
            key={route.path}
            path={route.path}
            element={
              <PublicLayout route={route}>
                <route.component />
              </PublicLayout>
            }
          />
        );
      })}

      {/* Public head routes (errors, standalone pages) */}
      {publicHeadRoutes.map(route => {
        if (!route.component) return null;
        return (
          <Router.Route
            key={route.path}
            path={route.path}
            element={
              <PublicLayout route={route}>
                <ChunkErrorBoundary>
                  <Suspense fallback={<RouteLoading />}>
                    <route.component />
                  </Suspense>
                </ChunkErrorBoundary>
              </PublicLayout>
            }
          />
        );
      })}

      {/* ──────────────────────────────────────────────────────────────── */
      /* PROTECTED ROUTES (authentication required)                     */
      /* ──────────────────────────────────────────────────────────────── */}

      <Router.Route path="/" element={<RequireAuth><AppShell /></RequireAuth>}>

        {/* Head Routes (static pages) */}
        {protectedHeadRoutes.map(route => {
          if (!route.component) return null;
          return (
            <Router.Route
              key={route.path}
              path={route.path}
              element={
                <GuardedRoute config={route}>
                  <ChunkErrorBoundary>
                    <Suspense fallback={<RouteLoading />}>
                      <route.component />
                    </Suspense>
                  </ChunkErrorBoundary>
                </GuardedRoute>
              }
            />
          );
        })}

        {/* Feature routes with per-route guards */}
        {featureRoutes.map(route => {
          if (!route.component) return null;
          const Component = route.component;
          return (
            <Router.Route
              key={route.path}
              path={route.path}
              element={
                <GuardedRoute config={route}>
                  <Component />
                </GuardedRoute>
              }
            />
          );
        })}

        {/* Index: redirect to correct landing page (mirrors Angular LoginStubComponent) */}
        <Router.Route index element={<CatchAllRedirect />} />

        {/* Catch-all → context-aware redirect (mirrors Angular) */}
        <Router.Route path="*" element={<CatchAllRedirect />} />
      </Router.Route>

      {/* Default: context-aware redirect for unknown routes */}
      <Router.Route path="*" element={<CatchAllRedirect />} />
    </Router.Routes>
  );
};

